import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  forceLight?: boolean;
}

const navLinks = [
  { label: 'Fragancias', href: '/fragrances' },
  { label: 'Nosotros', href: '/about' },
];

const navLinksRight = [
  { label: 'Noticias', href: '/news' },
  { label: 'Contacto', href: '/contact' },
];

export default function Navbar({ forceLight }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const allLinks = [...navLinks, ...navLinksRight];

  // Determine navbar style
  const isLight = forceLight || scrolled;

  const getNavStyle = () => {
    return isLight
      ? 'bg-white/95 backdrop-blur-sm shadow-sm'
      : 'bg-transparent';
  };

  const getTextColor = () => {
    return isLight ? 'text-gray-900' : 'text-white';
  };

  const getBurgerColor = () => {
    return isLight ? 'bg-gray-900' : 'bg-white';
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${getNavStyle()}`}
    >
      <div className="flex items-center justify-between px-6 md:px-10 lg:px-16 h-20">
        {/* Left Nav - Desktop */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm tracking-wide uppercase transition-colors duration-300 hover:opacity-70 ${getTextColor()}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/9df4a25d-3a45-400e-b6ef-a6922afe5ed4/b41d22fa-d180-46e1-9fb2-92fa040b6f94_222.png?v=28ae809954d7951413d6cac782f150b6"
            alt="Esencia&co."
            className="h-10 md:h-12 lg:h-14 w-auto object-contain"
          />
        </Link>

        {/* Right Nav - Desktop */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinksRight.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm tracking-wide uppercase transition-colors duration-300 hover:opacity-70 ${getTextColor()}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Mobile Burger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden flex flex-col gap-1.5 p-2 transition-colors duration-300 ${getTextColor()}`}
          aria-label="Toggle menu"
        >
          <span
            className={`block w-6 h-0.5 transition-all duration-300 ${
              mobileOpen ? 'rotate-45 translate-y-2' : ''
            } ${getBurgerColor()}`}
          />
          <span
            className={`block w-6 h-0.5 transition-all duration-300 ${
              mobileOpen ? 'opacity-0' : ''
            } ${getBurgerColor()}`}
          />
          <span
            className={`block w-6 h-0.5 transition-all duration-300 ${
              mobileOpen ? '-rotate-45 -translate-y-2' : ''
            } ${getBurgerColor()}`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-20 left-0 right-0 bg-white shadow-lg transition-all duration-500 overflow-hidden ${
          mobileOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="flex flex-col px-6 py-6 gap-4">
          {allLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="text-sm tracking-wide uppercase text-gray-900 hover:text-gray-500 transition-colors py-2 border-b border-gray-100"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
}